import { t } from "i18next";
import { fileUpload, get } from "../Services/api.service";
import { Paths } from "../Services/EndPoints";
import { isAllowed } from "./consent";

export const getNameInitials = ({ name }) => {
  if (!name) {
    return "";
  }
  return name
    .split(" ")
    .map((n) => n[0])
    .join("");
};

export const formatDate = (dateInput) => {
  const date = new Date(dateInput);
  const options = { day: "numeric", month: "long", year: "numeric" };
  return new Intl.DateTimeFormat("en-GB", options).format(date);
};

export function formatDateTimeNotifications(date) {
  if (date === null) {
    return "";
  }
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12;

  const minutesFormatted = minutes < 10 ? "0" + minutes : minutes;

  const timePart = `${hours}:${minutesFormatted} ${ampm}`;

  const datePart = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
    .format(date)
    .replace(/ /g, " ");

  return `${timePart} ${datePart}`;
}

// Razorpay's loader lives in Utils/loadRazorpay.js (loaded on demand at checkout only).

// PayU Checkout Plus's popup script. Two differences from the old loadRazorPayScript (now
// Utils/loadRazorpay.js, which follows the same rules):
//
// 1. The URL comes from the backend (PayUService.resolveBoltScriptUrl) rather than being hardcoded.
//    PayU ships separate test and live builds of this script, and each only works with a matching
//    test/live merchant key -- so hardcoding one would silently break payments in whichever
//    environment it didn't match.
// 2. It resolves false on error, instead of hanging. The caller needs to know a load failure
//    happened, because that's exactly the signal that triggers the redirect fallback.
export const loadPayUBoltScript = (scriptUrl) => {
  return new Promise((resolve) => {
    if (window.bolt) {
      resolve(true);
      return;
    }
    if (!scriptUrl) {
      resolve(false);
      return;
    }
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.onload = () => resolve(Boolean(window.bolt));
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const formatMobileNumber = (number) => {
  if (number?.length === 10) {
    return `${number?.slice(0, 5)} ${number?.slice(5, 10)}`;
  }
  return number;
};
export function getLabel(count, singular, plural) {
  return count === 1 ? singular : plural;
}

// Compact count for review/count chips, e.g. 1200 → "1.2k", 52400 → "52k".
export const formatCompactCount = (num) => {
  const n = Number(num || 0);
  if (n < 1000) return String(n);
  const k = n / 1000;
  return k < 10 ? `${k.toFixed(1).replace(/\.0$/, "")}k` : `${Math.round(k)}k`;
};

// API returns location fields in ALL CAPS (e.g. "ANDHRA PRADESH") — normalise.
export const titleCase = (str) => {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// "Visakhapatnam, Andhra Pradesh" from raw city/state.
export const formatLocation = (city, state) =>
  [titleCase(city), titleCase(state)].filter(Boolean).join(", ");

export const formatAmount = (amount, unit = "₹") => {
  if (isNaN(amount)) {
    return amount;
  }

  const num = Number(amount);

  return (
    unit +
    num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

export const createSlug = (text) => {
  if (!text) {
    return "";
  }
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

// Dedicated Wcommerce order-support WhatsApp line (distinct from the store
// owner's about_me number) — used by the order help / "Chat on WhatsApp" CTAs.
export const ORDER_SUPPORT_WHATSAPP = "8712015151";
export const orderSupportWhatsAppUrl = (message) =>
  `https://wa.me/91${ORDER_SUPPORT_WHATSAPP}${
    message ? `?text=${encodeURIComponent(message)}` : ""
  }`;

// Guest cart identity, shared by App.js (sets it once on app mount) and any page that
// needs a token to exist *immediately*, synchronously, regardless of App's own effect
// having run yet (React runs child effects before parent effects, so a route mounted
// straight onto a deep-linked URL — e.g. a shared custom-order/prescription link — can
// render before App's mount effect writes this). Idempotent: returns the existing token
// untouched if one's already stored, so calling it from multiple places never clobbers
// an in-progress guest session.
export const ensureGuestSessionToken = () => {
  const existing = sessionStorage.getItem("sessionToken");
  if (existing && existing !== "null") return existing;
  const timestamp = Date.now();
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let token = "";
  for (let i = 0; i < 10; i++) {
    token += characters[Math.floor(Math.random() * characters.length)];
  }
  const sessionToken = `${timestamp}${token}`;
  sessionStorage.setItem("sessionToken", sessionToken);
  return sessionToken;
};

const allowedImageTypes = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/heic",
];

export const validateImageFileAndUpload = (
  file,
  sizeLimit,
  openToast,
  setIsUploading,
  setPreviewUrl,
  setFieldValue,
  url = "upload_custom_print_image",
  index,
  side,
  afterUpload,
) => {
  if (typeof openToast !== "function") {
    console.error("openToast is not a function:", openToast);
    return false;
  }
  const isAllowed = allowedImageTypes.includes(file.type);
  const isLt5M = file.size / 1024 / 1024 < sizeLimit;
  if (!isAllowed) {
    openToast({
      content: t("upload.imageTypes"),
      type: "error",
    });
    return false;
  } else if (!isLt5M) {
    openToast({
      content: `Max image size allowed is ${sizeLimit} MB.`,
      type: "error",
    });
    return false;
  }
  return uploadImage(
    file,
    setIsUploading,
    setPreviewUrl,
    setFieldValue,
    url,
    index,
    side,
    afterUpload,
  );
};

export const uploadImage = async (
  file,
  setIsUploading,
  setPreviewUrl,
  setFieldValue,
  url,
  index,
  side,
  afterUpload,
) => {
  const formData = new FormData();
  formData.append(`files[0].file`, file?.originFileObj || file);
  formData.append(`files[0].mediaType`, file?.type || "image/png");
  formData.append(
    `files[0].fileThumbnail`,
    file?.thumbnailUrl || file?.originFileObj || file,
  );

  setIsUploading(true);
  await fileUpload(Paths?.[url], formData).then((res) => {
    setPreviewUrl((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      {
        index: index,
        url: res?.data[0]?.thumbnail,
      },
    ]);
    afterUpload(index, side, res?.data[0]?.thumbnail);
    setIsUploading(false);
    return res;
  });
};

// Meta's own cookies. _fbp identifies the browser, _fbc carries the ad click. They exist only
// here -- the server cannot derive them -- and without them a server-side event matches far more
// weakly to the ad that drove the sale. Sent to the backend at order-create so the server-side
// Purchase can include them even when it is triggered later by a payment webhook.
export const readMetaCookie = (name) => {
  // Split rather than build a RegExp from the name: the escaping in a string-built pattern is easy
  // to get subtly wrong (and silently matches nothing when it is).
  const entry = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(name + "="));
  return entry ? decodeURIComponent(entry.slice(name.length + 1)) : null;
};

export const readMetaBrowserIds = () => ({
  fbp: readMetaCookie("_fbp"),
  fbc: readMetaCookie("_fbc"),
});

// pwa.js injects the pixel only AFTER an async fetch for the store's pixel id, so
// an event fired during page load can run before window.fbq exists -- and used to
// be dropped silently, which is why the first ViewContent of a session went
// missing at random. Hold those and flush once the pixel is up.
//
// Consent-refused events are deliberately NOT queued: we do not back-fill
// tracking for actions taken before someone accepted.
const MAX_PENDING_META_EVENTS = 20;
const pendingMetaEvents = [];

const metaPixelReady = () =>
  typeof window !== "undefined" && !!window.fbq && !!window.currentMetaPixelId;

// method: "track" for Meta's standard events, "trackCustom" for our own names (PDP_BuyNow etc.) --
// a non-standard name sent via "track" is flagged by Meta as an error.
const fireMetaEvent = (method, eventName, params, eventId) => {
  if (eventId) {
    window.fbq(method, eventName, params, { eventID: eventId });
  } else {
    window.fbq(method, eventName, params);
  }
};

const flushMetaEvents = () => {
  if (!isAllowed("advertising")) {
    pendingMetaEvents.length = 0;
    return;
  }
  if (!metaPixelReady()) return;
  while (pendingMetaEvents.length) {
    const { method, eventName, params, eventId } = pendingMetaEvents.shift();
    fireMetaEvent(method, eventName, params, eventId);
  }
};

if (typeof window !== "undefined") {
  // pwa.js fires metapixelready after injection; consent.js fires consentchange.
  window.addEventListener("metapixelready", flushMetaEvents);
  window.addEventListener("consentchange", flushMetaEvents);
}

// eventId is Meta's DEDUPLICATION key: a browser event and a server event collapse into one sale
// only when event_name AND eventID both match. Both sides use the order number. Passing it is what
// stops the backend's Purchase from being counted as a second sale -- so the two must ship together.
// Carried through the pending-event queue too, so a Purchase queued before the pixel is ready still
// dedupes correctly once flushed.
//
// Returns true when the event was sent or queued, false when it was dropped for lack of
// advertising consent. A caller that de-dupes its own sends should only mark an event as
// delivered when this returns true.
const sendMetaEvent = (method, eventName, params, eventId) => {
  if (!isAllowed("advertising")) return false;
  if (metaPixelReady()) {
    fireMetaEvent(method, eventName, params, eventId);
    return true;
  }
  if (pendingMetaEvents.length < MAX_PENDING_META_EVENTS) {
    pendingMetaEvents.push({ method, eventName, params, eventId });
  }
  return true;
};

export const trackMetaEvent = (eventName, params, eventId) =>
  sendMetaEvent("track", eventName, params, eventId);

// Our own funnel events (PDP_BuyNow, PDP_GTC, ADDR_Continue). Same consent gate and queue.
export const trackMetaCustomEvent = (eventName, params) =>
  sendMetaEvent("trackCustom", eventName, params);

export const fetcher = (url) => get(url).then((res) => res?.data);

// Meta matches these ids against the ids in the seller's catalogue. Our catalogue
// item id is the SKU id -- backend does map.put("id", dto.getSkuId().toString()) --
// NOT the product id that sits in the URL path. Sent as strings because Meta
// compares them as strings.
export const buildMetaContent = ({
  skuId,
  quantity = 1,
  value,
  currency = "INR",
}) => ({
  content_type: "product",
  content_ids: [String(skuId)],
  contents: [{ id: String(skuId), quantity }],
  value,
  currency,
});

// Same shape for a basket. items = [{ skuId, quantity }].
export const buildMetaContents = ({ items = [], value, currency = "INR" }) => {
  const clean = items.filter((i) => i?.skuId != null);
  return {
    content_type: "product",
    content_ids: clean.map((i) => String(i.skuId)),
    contents: clean.map((i) => ({
      id: String(i.skuId),
      quantity: i.quantity ?? 1,
    })),
    value,
    currency,
  };
};
